import { useRef } from "react";
import { useChatStore, ChatMessage as ChatMessageType } from "@/zapi-frontend/stores/chatStore";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { reportErrorToSentry } from "@/zapi-frontend/utils/report-error-to-sentry";
import { confirmPollingEnd } from "@/zapi-frontend/shared/lib/confirmPollingEnd";
import { safeJsonParse } from "@/zapi-frontend/utils/safeJsonParse";
import { getStatusAll } from "../api/getStatus";
import { useQueryClient } from "@tanstack/react-query";
import { EGenerationStatus, EGenerationTaskType } from "../types";
import { getProgressMessage } from "../lib/getProgressMessage";
import { stripDelimiterLines } from "../lib/stripDelimiterLines";
import { useDiffStore } from "@/zapi-frontend/stores/diff";
import { getErrorConfig } from "@/zapi-frontend/shared/config/errorMessages";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";

function parseErrors(content: string): string[] | null {
  try {
    const parsed = JSON.parse(content);
    if (parsed.originalError?.message) return [parsed.originalError.message];
    if (parsed.error) return parsed.error.split(";");

    return null;
  } catch {
    return null;
  }
}

export function useChatPolling(projectId: string) {
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const requestInFlightRef = useRef(false);
  const processedTasksRef = useRef<Set<number>>(new Set());
  const { isLabs } = useMeStore();
  const queryClient = useQueryClient();

  const { addMessage, setLoadingState, resetLoadingState, updatePendingMessage, getLoadingState } = useChatStore();

  const { createPendingChanges } = useDiffStore();

  const { invalidateChapterCroppedImages, getChapterContent, projectChapters } = useChapterStore();

  const resolvePrereqChapterIds = (prereqNames: string[]): string[] =>
    prereqNames
      .map((name) => projectChapters.find((c) => c.projectTemplateChapter.name === name))
      .filter((c): c is NonNullable<typeof c> => !!c)
      .map((c) => String(c.id));

  const resetPrereqLoadingStates = (chapterId: string) => {
    const { allPrereqChapters } = getLoadingState(projectId, chapterId);
    if (allPrereqChapters) {
      resolvePrereqChapterIds(allPrereqChapters).forEach((prereqId) => {
        resetLoadingState(projectId, prereqId);
      });
    }
  };

  // The backend reports the prereqs still to be generated, so an original prereq missing from
  // that list is done — drop its spinner now rather than when the whole generation finishes.
  const releaseFinishedPrereqs = (chapterId: string, remainingPrereqs: string[]) => {
    const { allPrereqChapters } = getLoadingState(projectId, chapterId);
    if (!allPrereqChapters) return;
    const finishedPrereqs = allPrereqChapters.filter((name) => !remainingPrereqs.includes(name));
    resolvePrereqChapterIds(finishedPrereqs).forEach((prereqId) => {
      const prereqState = getLoadingState(projectId, prereqId);
      if (prereqState.isGeneratingChapter && !prereqState.pendingGenerationId) {
        resetLoadingState(projectId, prereqId);
      }
    });
  };

  const pollGenerationStatus = async () => {
    if (requestInFlightRef.current) {
      return;
    }
    requestInFlightRef.current = true;
    try {
      const _statuses = await queryClient.fetchQuery({
        queryKey: ["chat-status-all", projectId],
        queryFn: () => getStatusAll(projectId)
      });

      const chapterStatuses = _statuses
        .filter((status) => status.type === EGenerationTaskType.chapter && !!status.chapterId)
        .filter((status) => status.id === getLoadingState(projectId, String(status.chapterId)).pendingGenerationId);

      const statuses = [...chapterStatuses];
      const promises = statuses.map(async (statusData) => {
        if (processedTasksRef.current.has(statusData.id)) {
          return;
        }

        const chapterId = statusData.chapterId?.toString() || "";
        const storeKey = chapterId;

        switch (statusData.status) {
          case EGenerationStatus.pending:
          case EGenerationStatus.processing: {
            const prereqs = statusData.prereqs;
            if (prereqs && prereqs.length > 0) {
              const existing = getLoadingState(projectId, storeKey).allPrereqChapters;
              setLoadingState(projectId, storeKey, {
                prereqsChaptersToBeGenerated: prereqs,
                ...(!existing && { allPrereqChapters: prereqs })
              });
              resolvePrereqChapterIds(prereqs).forEach((prereqId) => {
                setLoadingState(projectId, prereqId, { isGeneratingChapter: true, isChapterLoaded: false });
              });
            } else if (prereqs) {
              setLoadingState(projectId, storeKey, { prereqsChaptersToBeGenerated: [] });
            }
            if (prereqs) releaseFinishedPrereqs(storeKey, prereqs);
            break;
          }

          case EGenerationStatus.streaming: {
            const prereqs = statusData.prereqs;
            const existing = getLoadingState(projectId, storeKey).allPrereqChapters;

            console.log("Progress: ", getProgressMessage(statusData.createdAt));
            setLoadingState(projectId, storeKey, {
              isGeneratingChapter: true,
              progressMessage: getProgressMessage(statusData.createdAt),
              ...(prereqs !== undefined && { prereqsChaptersToBeGenerated: prereqs }),
              ...(!existing && prereqs && prereqs.length > 0 && { allPrereqChapters: prereqs })
            });

            if (prereqs && prereqs.length > 0) {
              resolvePrereqChapterIds(prereqs).forEach((prereqId) => {
                setLoadingState(projectId, prereqId, { isGeneratingChapter: true, isChapterLoaded: false });
              });
            } else if (statusData.result) {
              try {
                const progressData = safeJsonParse(statusData.result);
                if (progressData.type === "chapterGeneration") {
                  setLoadingState(projectId, storeKey, {
                    isGeneratingChapter: true,
                    progressMessage: getProgressMessage(statusData.createdAt)
                  });
                }
              } catch (error) {
                console.error("Error parsing progress data:", error);
              }
            }
            if (prereqs) releaseFinishedPrereqs(storeKey, prereqs);
            break;
          }

          case EGenerationStatus.ready: {
            const loadingState = getLoadingState(projectId, storeKey);
            const allPrereqs = loadingState.allPrereqChapters;
            await confirmPollingEnd(statusData.id);
            processedTasksRef.current.add(statusData.id);
            resetPrereqLoadingStates(storeKey);
            if (statusData.result) {
              await handleGenerationCompleteForChapter(storeKey, statusData.result, !!allPrereqs?.length);
            }
            if (allPrereqs?.length) {
              addMessage(projectId, storeKey, {
                role: "assistant",
                id: `prereq-summary-${statusData.id}`,
                content: JSON.stringify({ type: "prereqSummary", content: "", chapters: allPrereqs })
              });
              addMessage(projectId, storeKey, {
                role: "assistant",
                id: `all-chapters-ready-${statusData.id}`,
                content: JSON.stringify({ type: "answer", content: "All chapters generated. Your analysis is ready!" })
              });
            }
            break;
          }

          case EGenerationStatus.error: {
            await confirmPollingEnd(statusData.id);
            processedTasksRef.current.add(statusData.id);
            resetPrereqLoadingStates(storeKey);
            const errors = statusData.result ? parseErrors(statusData.result) : null;

            let humanReadableError: string | undefined;
            if (errors && errors.length > 0) {
              const errorConfig = getErrorConfig(errors[0], !!isLabs);
              humanReadableError = errorConfig.userMessage;
              reportErrorToSentry(errors[0], "Chat Island Error", errorConfig.errorKey, {
                projectId,
                chapterId: storeKey,
                result: statusData.result
              });
            }

            handleGenerationErrorForChapter(storeKey, humanReadableError || "Generation failed. Please try again.");

            break;
          }

          case EGenerationStatus.cancelled:
            processedTasksRef.current.add(statusData.id);
            resetPrereqLoadingStates(storeKey);
            resetLoadingState(projectId, storeKey);
            break;

          case EGenerationStatus.processed:
            console.error("Unexpected processed:", statusData.id);
            break;
          default:
            console.error("Unknown generation status:", statusData.status);
        }
      });
      await Promise.all(promises);
    } catch (error) {
      console.error("Error polling generation status:", error);
      return;
    } finally {
      requestInFlightRef.current = false;
    }
  };

  const handleGenerationCompleteForChapter = async (
    chapterId: string,
    result: string,
    suppressAgentMessage = false
  ) => {
    if (!chapterId) return;

    const loadingState = getLoadingState(projectId, chapterId);
    const botMessageId = loadingState.loadingMessageId || `bot-${Date.now()}`;

    try {
      if (result.startsWith("CHAPTER_GEN::")) {
        const delimiterIndex = result.indexOf("---CONTENT---");
        if (delimiterIndex === -1) {
          throw new Error("Invalid CHAPTER_GEN format: missing ---CONTENT--- delimiter");
        }

        const headerPart = result.substring("CHAPTER_GEN::".length, delimiterIndex).trim();
        const rawContent = stripDelimiterLines(result.substring(delimiterIndex + "---CONTENT---".length + 1));
        const headerLines = headerPart.split("\n");
        const agentMessage = headerLines[0] || "";
        const agentType = headerLines[1] || "";

        const imageRegex = /!\[.*?\]\(.*?\)/g;
        if (imageRegex.test(rawContent)) {
          try {
            await invalidateChapterCroppedImages(chapterId);
          } catch (error) {
            console.error("Failed to invalidate chapter cropped images:", error);
          }
        }

        createPendingChanges(projectId, chapterId, rawContent, getChapterContent(chapterId) || "");

        const agentBubble: ChatMessageType = {
          role: "assistant",
          content: JSON.stringify({ type: "agent", content: agentMessage, agentType, notes: [] }),
          id: `action-${Date.now()}-0`
        };

        updatePendingMessage(projectId, chapterId, result);
        if (!suppressAgentMessage) {
          addMessage(projectId, chapterId, agentBubble);
        }

        resetLoadingState(projectId, chapterId);
        return;
      }

      if (result.startsWith("CHAT_RESPONSE::")) {
        const contentDelimiter = "---CONTENT---";
        const delimiterIndex = result.indexOf(contentDelimiter);
        if (delimiterIndex === -1) {
          throw new Error("Invalid CHAT_RESPONSE format: missing ---CONTENT--- delimiter");
        }

        const headerPart = result.substring("CHAT_RESPONSE::".length, delimiterIndex).trim();
        const rawContent = stripDelimiterLines(result.substring(delimiterIndex + contentDelimiter.length + 1));
        const headerLines = headerPart.split("\n");
        const responseType = headerLines[0] || "";
        const notes = headerLines.slice(1).filter(Boolean);

        if (responseType === "answerAndUpdate") {
          const answerDelimiter = "---ANSWER---";
          const answerIndex = headerPart.indexOf(answerDelimiter);
          const notesBlock = answerIndex !== -1 ? headerPart.substring(responseType.length, answerIndex) : "";
          const combinedNotes = notesBlock
            .split("\n")
            .map((n) => n.trim())
            .filter(Boolean);
          const answer = answerIndex !== -1 ? headerPart.substring(answerIndex + answerDelimiter.length).trim() : "";

          const imageRegex = /!\[.*?\]\(.*?\)/g;
          if (imageRegex.test(rawContent)) {
            try {
              await invalidateChapterCroppedImages(chapterId);
            } catch (error) {
              console.error("Failed to invalidate chapter cropped images:", error);
            }
          }

          createPendingChanges(projectId, chapterId, rawContent, getChapterContent(chapterId) || "");

          const chapterName = projectChapters.find((c) => String(c.id) === chapterId)?.projectTemplateChapter?.name;
          const combinedBubble: ChatMessageType = {
            role: "assistant",
            content: JSON.stringify({ type: "answerAndUpdate", content: answer, notes: combinedNotes, chapterName }),
            id: botMessageId
          };

          updatePendingMessage(projectId, chapterId, result);
          addMessage(projectId, chapterId, combinedBubble);
        } else if (responseType === "updateChapter") {
          const imageRegex = /!\[.*?\]\(.*?\)/g;
          if (imageRegex.test(rawContent)) {
            try {
              await invalidateChapterCroppedImages(chapterId);
            } catch (error) {
              console.error("Failed to invalidate chapter cropped images:", error);
            }
          }

          createPendingChanges(projectId, chapterId, rawContent, getChapterContent(chapterId) || "");

          const chapterName = projectChapters.find((c) => String(c.id) === chapterId)?.projectTemplateChapter?.name;
          const updateBubble: ChatMessageType = {
            role: "assistant",
            content: JSON.stringify({ type: "updateChapter", content: "", notes, chapterName }),
            id: botMessageId
          };

          updatePendingMessage(projectId, chapterId, result);
          addMessage(projectId, chapterId, updateBubble);
        } else if (responseType === "error") {
          const errorBubble: ChatMessageType = {
            role: "assistant",
            content: JSON.stringify({ type: "error", content: rawContent }),
            id: botMessageId
          };
          addMessage(projectId, chapterId, errorBubble);
        } else {
          const answerBubble: ChatMessageType = {
            role: "assistant",
            content: JSON.stringify({ type: "answer", content: rawContent }),
            id: botMessageId
          };
          updatePendingMessage(projectId, chapterId, result);
          addMessage(projectId, chapterId, answerBubble);
        }

        resetLoadingState(projectId, chapterId);
        return;
      }

      throw new Error(`Unrecognized response format: ${result.substring(0, 50)}`);
    } catch (error) {
      console.error("Error handling generation complete:", error);
      reportErrorToSentry(
        "Error handling generation complete",
        "Chat Island Error",
        "error_handling_generation_complete",
        {
          error: error,
          result: result
        }
      );
      handleGenerationErrorForChapter(chapterId, "Error processing generation result");
    }
  };

  // Error message is already processed in confirmPolling
  const handleGenerationErrorForChapter = (chapterId: string, errorMessage: string) => {
    if (!chapterId) return;

    const loadingState = getLoadingState(projectId, chapterId);
    const botMessageId = loadingState.loadingMessageId || `bot-${Date.now()}`;

    const errorContent = JSON.stringify({
      type: "error",
      content: errorMessage
    });

    const errorBotMessage: ChatMessageType = {
      role: "assistant",
      content: errorContent,
      id: botMessageId
    };
    if (loadingState.pendingGenerationId) {
      addMessage(projectId, chapterId, errorBotMessage);
    }
    resetLoadingState(projectId, chapterId);
  };

  const startPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    pollGenerationStatus();
    pollingIntervalRef.current = setInterval(() => {
      pollGenerationStatus();
    }, 4000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  return {
    startPolling,
    stopPolling,
    pollOnce: pollGenerationStatus,
    resetPrereqLoadingStates
  };
}
